import React, { memo, useMemo } from 'react'

interface Props {
  text: string | undefined | null
  className?: string
  style?: React.CSSProperties
}

// `stripMarkdown` moved to `~/utils/stripMarkdown` — it's a pure plaintext
// utility used by both this component and by TTS/title call sites unrelated
// to ProgressMarkdown's tokenizer.

interface Token {
  type:
    | 'text'
    | 'bold'
    | 'italic'
    | 'code'
    | 'strikethrough'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'hr'
    | 'linebreak'
  content: string
}

/**
 * Parses a single line for block-level markdown (headers, hr).
 * Returns tokens for the line.
 */
function parseLine(line: string): Token[] {
  // Skip empty lines - they become linebreaks
  if (!line.trim()) {
    return []
  }

  // Horizontal rule: --- or —— or ——— (em dashes)
  if (/^(-{3,}|—{2,})$/.test(line.trim())) {
    return [{ type: 'hr', content: '' }]
  }

  // Headers: # H1, ## H2, ### H3 (must be at start of line)
  const headerMatch = line.match(/^(#{1,3})\s+(.*)$/)
  if (headerMatch) {
    const level = headerMatch[1].length
    const content = headerMatch[2]
    const type = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3'
    return [{ type, content }]
  }

  // Otherwise, parse for inline formatting
  return tokenizeInline(line)
}

/**
 * Tokenizes text with markdown patterns.
 * Supports: **bold**, *italic*, `code`, ~~strikethrough~~, # headers, --- hr
 * Handles multi-line text by splitting on newlines.
 */
function tokenize(text: string | undefined | null): Token[] {
  if (!text) return []

  // Check if text contains newlines - if so, process line by line
  if (text.includes('\n')) {
    const lines = text.split('\n')
    const allTokens: Token[] = []

    for (let i = 0; i < lines.length; i++) {
      const lineTokens = parseLine(lines[i])
      allTokens.push(...lineTokens)

      // Add linebreak between non-empty content (but not after last line)
      if (i < lines.length - 1 && lineTokens.length > 0) {
        allTokens.push({ type: 'linebreak', content: '' })
      }
    }

    return allTokens
  }

  // Single line - use parseLine directly
  return parseLine(text)
}

/**
 * Tokenizes inline markdown patterns within text.
 */
function tokenizeInline(text: string): Token[] {
  const tokens: Token[] = []

  // Pattern matches: **bold**, *italic*, `code`, ~~strikethrough~~
  // Order matters: ** before * to avoid conflicts
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|~~[^~]+~~)/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      })
    }

    const matched = match[0]

    if (matched.startsWith('**') && matched.endsWith('**')) {
      tokens.push({
        type: 'bold',
        content: matched.slice(2, -2)
      })
    } else if (matched.startsWith('~~') && matched.endsWith('~~')) {
      tokens.push({
        type: 'strikethrough',
        content: matched.slice(2, -2)
      })
    } else if (matched.startsWith('`') && matched.endsWith('`')) {
      tokens.push({
        type: 'code',
        content: matched.slice(1, -1)
      })
    } else if (matched.startsWith('*') && matched.endsWith('*')) {
      tokens.push({
        type: 'italic',
        content: matched.slice(1, -1)
      })
    }

    lastIndex = pattern.lastIndex
  }

  // Add remaining text
  if (lastIndex < text.length) {
    tokens.push({
      type: 'text',
      content: text.slice(lastIndex)
    })
  }

  return tokens
}

const tokenStyles: Record<Token['type'], React.CSSProperties> = {
  text: {},
  bold: { fontWeight: 800 },
  italic: { fontStyle: 'italic' },
  code: {
    fontFamily: 'monospace',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    padding: '1px 4px',
    borderRadius: '3px',
    fontSize: '0.9em'
  },
  strikethrough: { textDecoration: 'line-through' },
  h1: { fontWeight: 700, fontSize: '1.3em' },
  h2: { fontWeight: 700, fontSize: '1.15em' },
  h3: { fontWeight: 700, fontSize: '1.05em' },
  hr: {},
  linebreak: {}
}

/**
 * Renders a single token as a React element.
 */
function renderToken(token: Token, index: number): React.ReactNode {
  switch (token.type) {
    case 'hr':
      return (
        <hr
          key={index}
          style={{ border: 'none', borderTop: '1px solid rgba(0, 0, 0, 0.15)', margin: '4px 0' }}
        />
      )
    case 'linebreak':
      return <br key={index} />
    default:
      return (
        <span key={index} style={tokenStyles[token.type]}>
          {token.content}
        </span>
      )
  }
}

/**
 * Renders inline markdown as styled spans.
 * Lightweight alternative to full markdown for UI elements like progress messages.
 *
 * Supports: **bold**, *italic*, `code`, ~~strikethrough~~, # headers, --- hr
 * Handles multi-line text with proper line breaks.
 *
 * Uses inline styles to override any parent CSS.
 */
const ProgressMarkdown = memo(({ text, className, style }: Props) => {
  const tokens = useMemo(() => tokenize(text), [text])

  // Fast path: empty or no markdown found
  if (!text || tokens.length === 0) {
    return (
      <span className={className} style={style}>
        {text ?? ''}
      </span>
    )
  }

  if (tokens.length === 1 && tokens[0].type === 'text') {
    return (
      <span className={className} style={style}>
        {text}
      </span>
    )
  }

  // Check if we have block-level elements (hr, headers, linebreaks)
  const hasBlockElements = tokens.some(
    t => t.type === 'hr' || t.type === 'linebreak' || t.type.startsWith('h')
  )

  return (
    <span
      className={className}
      style={{ ...style, ...(hasBlockElements ? { display: 'block' } : {}) }}
    >
      {tokens.map(renderToken)}
    </span>
  )
})

ProgressMarkdown.displayName = 'ProgressMarkdown'

export default ProgressMarkdown
